import React, { useState, useEffect } from 'react';
import { Modal, Input, DatePicker, Form, message } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, CalendarOutlined } from '@ant-design/icons';
import { CancelButton, CreateButton } from '../../buttons';
import { Student } from '../../tables/StudentsTable';
import * as S from './StudentModal.styles';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Extend dayjs with required plugins for antd DatePicker
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);

export interface StudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (studentData: Partial<Student>) => void;
  student?: Student | null;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

export const StudentModal: React.FC<StudentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  student,
  isLoading = false,
  mode
}) => {
  const [form] = Form.useForm();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: ''
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && student) {
        // Parse date safely - handle various formats from server
        let parsedDate = null;
        if (student.dateOfBirth) {
          // Try parsing with common formats
          const dateStr = student.dateOfBirth;
          if (dayjs(dateStr, 'YYYY-MM-DD', true).isValid()) {
            parsedDate = dayjs(dateStr, 'YYYY-MM-DD');
          } else if (dayjs(dateStr, 'DD/MM/YYYY', true).isValid()) {
            parsedDate = dayjs(dateStr, 'DD/MM/YYYY');
          } else if (dayjs(dateStr, 'MM/DD/YYYY', true).isValid()) {
            parsedDate = dayjs(dateStr, 'MM/DD/YYYY');
          } else if (dayjs(dateStr).isValid()) {
            // Try auto-parsing (handles ISO strings)
            parsedDate = dayjs(dateStr);
          }
        }
        
        const data = {
          name: student.name || '',
          email: student.email || '',
          phone: student.phone || '',
          dateOfBirth: student.dateOfBirth || ''
        };
        setFormData(data);
        form.setFieldsValue({
          ...data,
          dateOfBirth: parsedDate
        });
      } else {
        const emptyData = { name: '', email: '', phone: '', dateOfBirth: '' };
        setFormData(emptyData);
        form.setFieldsValue({
          ...emptyData,
          dateOfBirth: null
        });
      }
    }
  }, [isOpen, mode, student, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const submitData = {
        ...values,
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format('YYYY-MM-DD') : undefined,
        ...(mode === 'edit' && student ? { id: student.id } : {})
      };
      onSubmit(submitData);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setFormData({ name: '', email: '', phone: '', dateOfBirth: '' });
    onClose();
  };

  const isFormValid = formData.name.trim().length > 0;

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={360}
      centered
      destroyOnClose
    >
      <S.ModalContent>
        <S.IconWrapper>
          <UserOutlined />
        </S.IconWrapper>
        
        <S.Title>
          {mode === 'create' ? 'Add New Student' : 'Edit Student'}
        </S.Title>
        
        <S.Subtitle>
          {mode === 'create' 
            ? 'Add a new student to this class and start tracking their progress'
            : 'Update student information and details'
          }
        </S.Subtitle>

        <Form
          form={form}
          layout="vertical"
          onValuesChange={(_, allValues) => {
            setFormData(prev => ({
              ...prev,
              name: allValues.name || '',
              email: allValues.email || '',
              phone: allValues.phone || ''
            }));
          }}
        >
          <S.FormSection>
            <S.FormField>
              <S.Label>
                <UserOutlined />
                Student Name *
              </S.Label>
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: 'Please enter student name' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      
                      const words = value.trim().split(/\s+/);
                      
                      if (words.length < 2) {
                        return Promise.reject('Please enter first name and last name (2 words)');
                      }
                      
                      const firstName = words[0];
                      const lastName = words.slice(1).join(' ');
                      
                      if (firstName.length < 2) {
                        return Promise.reject('First name must be at least 2 characters');
                      }
                      
                      if (lastName.length < 2) {
                        return Promise.reject('Last name must be at least 2 characters');
                      }
                      
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <S.StyledInput 
                  placeholder="e.g. John Smith"
                  maxLength={50}
                />
              </Form.Item>
              <S.FieldHint>Enter first name and last name separated by space</S.FieldHint>
            </S.FormField>

            <S.FormField>
              <S.Label>
                <MailOutlined />
                Email Address
              </S.Label>
              <Form.Item
                name="email"
                rules={[
                  { type: 'email', message: 'Please enter a valid email address' }
                ]}
              >
                <S.StyledInput 
                  placeholder="e.g. john.smith@email.com"
                  maxLength={100}
                />
              </Form.Item>
            </S.FormField>

            <S.FormField>
              <S.Label>
                <PhoneOutlined />
                Phone Number
              </S.Label>
              <Form.Item 
                name="phone"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') return Promise.resolve();
                      
                      // Phone must start with + and contain only digits after
                      const phoneRegex = /^\+\d+$/;
                      if (!phoneRegex.test(value.replace(/\s/g, ''))) {
                        return Promise.reject('Phone must start with + followed by digits only (e.g. +1234567890)');
                      }
                      
                      // Minimum 10 digits
                      const digitsOnly = value.replace(/\D/g, '');
                      if (digitsOnly.length < 10) {
                        return Promise.reject('Phone number must have at least 10 digits');
                      }
                      
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <S.StyledInput 
                  placeholder="e.g. +12345678901"
                  maxLength={20}
                />
              </Form.Item>
              <S.FieldHint>Format: +[country code][number] (e.g. +12345678901)</S.FieldHint>
            </S.FormField>

            <S.FormField>
              <S.Label>
                <CalendarOutlined />
                Date of Birth
              </S.Label>
              <Form.Item name="dateOfBirth">
                <S.StyledDatePicker 
                  placeholder="Select date of birth"
                  format="YYYY-MM-DD"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </S.FormField>
          </S.FormSection>
        </Form>

        <S.ButtonSection>
          <CancelButton
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </CancelButton>

          <CreateButton
            onClick={handleSubmit}
            disabled={isLoading || !isFormValid}
            loading={isLoading}
          >
            {mode === 'create' ? 'Add Student' : 'Save Changes'}
          </CreateButton>
        </S.ButtonSection>
      </S.ModalContent>
    </Modal>
  );
};
